"use client";

/**
 * Client-side providers wrapper.
 *
 * This file is marked "use client" so that Redux's Provider component
 * (which requires browser APIs) can wrap the whole app without making
 * layout.tsx itself a client component.
 *
 * Pattern explanation: Next.js App Router renders the page tree on the
 * server first. By keeping layout.tsx as a Server Component and wrapping
 * only the providers in a Client Component, we give Next.js the most
 * flexibility to optimize rendering.
 */

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { isPrivateExplanationPath } from "@/lib/explain/privacy";
import { Provider as ReduxStoreProvider } from "react-redux";
import { store } from "@/lib/store";
import { sceneActions } from "@/lib/store/slices/scene";
import { installConsoleBuffer } from "@/lib/diagnostics/console-buffer";
import { installFetchBuffer } from "@/lib/diagnostics/network-buffer";

function InstallDiagnostics() {
  useEffect(() => {
    // One-time client-side install of the bug-report diagnostic buffers
    // (console ring + failed-fetch ring). Both are idempotent, so a
    // StrictMode double-mount is harmless.
    installConsoleBuffer();
    installFetchBuffer();
  }, []);
  return null;
}

function ExposeStoreForTests() {
  useEffect(() => {
    // PERF-DIAGNOSTIC (temporary): widen gate so window.__TEST__ survives a
    // prod build when NEXT_PUBLIC_ENABLE_TEST_API=true is set at build time.
    // Inert in a normal prod build (flag unset → folds back to the NODE_ENV
    // check, whole block dead-code-eliminated). REVERT after the perf sweep.
    if (process.env.NODE_ENV !== "production" || process.env.NEXT_PUBLIC_ENABLE_TEST_API === "true") {
      (window as any).__REDUX_STORE__ = store;
      (window as any).__SCENE_ACTIONS__ = { sceneActions };

      // Initialize the dev-mode test API only when explicitly enabled via
      // NEXT_PUBLIC_ENABLE_TEST_API=true in .env.local (gitignored).
      // This ensures the API is only available on machines that opt in.
      if (process.env.NEXT_PUBLIC_ENABLE_TEST_API === "true") {
        import("@/lib/testing/test-api").then(({ createTestAPI }) => {
          window.__TEST__ = createTestAPI(store);
          console.log("[VAON] Test API ready — window.__TEST__.help() for usage");
        });
      }
    }
    return () => {
      delete window.__TEST__;
    };
  }, []);
  return null;
}

export function ReduxProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  if (isPrivateExplanationPath(pathname)) return <>{children}</>;
  return (
    <ReduxStoreProvider store={store}>
      <InstallDiagnostics />
      <ExposeStoreForTests />
      {children}
    </ReduxStoreProvider>
  );
}
